const SCAN_ROUTE_PREFIX = '/scan/';
const PROD_SCAN_HOST = 'hogc.one';
const MOBILE_APP_HOSTS = new Set([
  'mobile.heartofgodchurch.org',
  'mobile.dev.heartofgodchurch.org',
  'mobile.stg.heartofgodchurch.org',
]);

export function isScanRouteHost(hostname) {
  if (typeof hostname !== 'string') return false;
  return hostname === PROD_SCAN_HOST || MOBILE_APP_HOSTS.has(hostname);
}

const coerceString = value => {
  if (Array.isArray(value)) return value[0] || '';
  if (value === null || value === undefined) return '';
  return String(value);
};

const safeDecode = value => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export function queryEntriesToObject(entries) {
  const query = {};
  for (const [key, value] of entries) {
    query[key] = value;
  }
  return query;
}

export function scannerPayloadFromPath(path, query = {}) {
  if (typeof path !== 'string') return null;

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!normalizedPath.startsWith(SCAN_ROUTE_PREFIX)) return null;

  const segments = normalizedPath.split('/').filter(Boolean);
  if (segments.length < 4 || segments[0] !== 'scan') return null;

  const [, type, action, ...refSegments] = segments;
  const ref = refSegments.join('/');
  if (!type || !action || !ref) return null;

  const normalizedQuery = {};
  for (const [key, value] of Object.entries(query || {})) {
    const stringValue = coerceString(value);
    if (stringValue) normalizedQuery[key] = stringValue;
  }

  return {
    type: safeDecode(type),
    action: safeDecode(action),
    ref: safeDecode(ref),
    query: normalizedQuery,
  };
}

export function scannerPayloadFromUrl(rawValue) {
  if (typeof rawValue !== 'string') return null;

  try {
    const url = new URL(rawValue);
    if (!isScanRouteHost(url.hostname)) return null;
    return scannerPayloadFromPath(url.pathname, queryEntriesToObject(url.searchParams.entries()));
  } catch {
    return null;
  }
}

export function scannerPayloadFromRouteQuery(routeQuery = {}) {
  const type = coerceString(routeQuery.type);
  const action = coerceString(routeQuery.action);
  const ref = coerceString(routeQuery.ref);
  if (!type || !action || !ref) return null;

  const query = {};
  for (const [key, value] of Object.entries(routeQuery)) {
    if (key === 'type' || key === 'action' || key === 'ref') continue;
    const stringValue = coerceString(value);
    if (stringValue) query[key] = stringValue;
  }

  return { type, action, ref, query };
}

/**
 * The one scanner payload that is not a scanner payload. `/scan/course/redeem/<code>`
 * is printed on physical cards, and a card already cut cannot be recalled — so
 * the path stays in the /scan/ family while the destination does not.
 */
export function isCourseRedeem(payload) {
  return payload?.type === 'course' && payload?.action === 'redeem' && !!payload?.ref;
}

/**
 * A leader's unlock QR: `/scan/course/leader-unlock/<token>`, where `ref` IS the
 * token.
 *
 * Its own action rather than `unlock`, which belongs to the class QR and carries a
 * PROGRAM SLUG as its ref — telling those apart by inspecting the ref would mean
 * guessing.
 */
export function isCourseLeaderUnlock(payload) {
  return payload?.type === 'course' && payload?.action === 'leader-unlock' && !!payload?.ref;
}

/**
 * An unlock link that puts the code in a query string rather than the path:
 * `/scan/course/redeem/?code=IDRU-...`. That shape predates the printed cards
 * and is wrong — IDR's own guidance is to read the code off the path — but it
 * is already out in messages and chats, and a link someone already sent cannot
 * be recalled any more than a card already cut can.
 *
 * `scannerPayloadFromPath` needs four segments and returns null for it, so
 * classifyDeepLink fell through to the slug lookup and landed the member on
 * Home with no explanation at all. Accepting it here costs nothing and turns a
 * silent dead end into a redemption.
 *
 * Deliberately narrow: course/redeem only, and only when the path itself
 * carries no ref. No other scan lane gains a query-string form.
 */
function courseRedeemFromQuery(path, query = {}) {
  if (typeof path !== 'string') return null;

  const segments = path.split('/').filter(Boolean);
  if (segments.length !== 3) return null;
  const [scan, type, action] = segments;
  if (scan !== 'scan' || type !== 'course' || action !== 'redeem') return null;

  const ref = coerceString(query.code) || coerceString(query.ref);
  if (!ref) return null;

  const rest = { ...query };
  delete rest.code;
  delete rest.ref;

  return { type: 'course', action: 'redeem', ref: safeDecode(ref), query: rest };
}

export function buildScannerRouteFromPath(path, originalUrl, options = {}) {
  let hash = '';
  let query = {};

  if (originalUrl) {
    try {
      const url = new URL(originalUrl);
      if (!options.trustPath && !isScanRouteHost(url.hostname)) return null;
      hash = url.hash;
      query = queryEntriesToObject(url.searchParams.entries());
    } catch {
      query = {};
    }
  }

  const payload = scannerPayloadFromPath(path, query) || courseRedeemFromQuery(path, query);
  if (!payload) return null;

  // Unlock cards get their own screen. This is the branch that matters for a
  // real card: a universal link opened from the phone's camera resolves here,
  // NOT through the /scan/ router redirect, so sending it to /scanner would put
  // a viewfinder in front of the member while an invisible redemption ran.
  if (isCourseRedeem(payload)) {
    return {
      path: `/courses/redeem/${encodeURIComponent(payload.ref)}`,
      query: payload.query,
      hash,
    };
  }

  // A leader's unlock QR, for exactly the reason above — and this branch is why
  // the comment above matters. It was added to the /scan/ ROUTER REDIRECT first
  // and only there, which a camera scan never reaches on a cold start. The scan
  // fell through to /scanner, and because `leader-unlock` is not in that route's
  // flag carve-out, members whose `scanner` flag is off were bounced to /my-qr.
  if (isCourseLeaderUnlock(payload)) {
    return {
      path: `/courses/unlock/${encodeURIComponent(payload.ref)}`,
      query: payload.query,
      hash,
    };
  }

  return {
    path: '/scanner',
    query: {
      type: payload.type,
      action: payload.action,
      ref: payload.ref,
      ...payload.query,
    },
    hash,
  };
}

const SSO_APPROVE_PREFIX = '/sso/approve/';

/**
 * The SSO approval QR shown on a sister app's DESKTOP login screen, e.g.
 *   https://mobile.dev.heartofgodchurch.org/sso/approve/<requestId>?code=<code>
 *
 * Cross-device SSO: a member on a laptop cannot approve there, so the laptop
 * shows this QR and the member scans it with the HOGC One app on their phone.
 * The camera reading it off the screen is the binding — a forwarded link does
 * not work, which is the whole point of the code riding inside the QR.
 *
 * Kept entirely separate from the /scan/ scanner contract: this is an in-app
 * navigation to the approval page, not a backend scanner call.
 */
export function ssoApprovalFromPath(path, query = {}) {
  if (typeof path !== 'string') return null;

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!normalizedPath.startsWith(SSO_APPROVE_PREFIX)) return null;

  const segments = normalizedPath.split('/').filter(Boolean);
  // Exactly ['sso', 'approve', '<requestId>']. The request id is a UUID, so it
  // never contains a slash; anything longer is not an approval link.
  if (segments.length !== 3 || segments[0] !== 'sso' || segments[1] !== 'approve') {
    return null;
  }

  const requestId = safeDecode(segments[2]);
  if (!requestId) return null;

  // The pairing code the member must present. Absent is tolerated so a malformed
  // QR still routes to the page, which then refuses the approval server-side.
  return { requestId, code: coerceString(query.code) };
}

export function ssoApprovalFromUrl(rawValue) {
  if (typeof rawValue !== 'string') return null;

  try {
    const url = new URL(rawValue);
    // Only the app's own hosts issue these; hogc.one (the /scan/ host) never
    // does, so it is deliberately excluded here.
    if (!MOBILE_APP_HOSTS.has(url.hostname)) return null;
    return ssoApprovalFromPath(url.pathname, queryEntriesToObject(url.searchParams.entries()));
  } catch {
    return null;
  }
}
